// Changes we've sent to the site's ops tablet for approval, and what happened to them.

import { useState } from 'react';
import { api, message } from '../../api.ts';
import { when } from '../../format.ts';
import type { SiteView } from '../../types.ts';

export function Changes({ site, reload }: { site: SiteView; reload: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const waiting = site.changes.find((c) => c.status === 'waiting');
  const earlier = site.changes.filter((c) => c.status !== 'waiting');
  const withdraw = async (id: string) => {
    if (!confirm('Withdraw this change? It will no longer be on the ops tablet to approve.')) return;
    try {
      await api('POST', `/changes/${id}/withdraw`, {});
      reload();
    } catch (e) {
      setError(message(e));
    }
  };
  const tone = { approved: 'green', declined: 'red', withdrawn: 'faint', waiting: 'amber' } as const;
  const word = { approved: 'Approved', declined: 'Declined', withdrawn: 'Withdrawn', waiting: 'Waiting' } as const;
  return (
    <div className="stack" style={{ gap: 22, maxWidth: 960 }}>
      {error && <p className="error">{error}</p>}
      {waiting ? (
        <div className="card" style={{ borderColor: '#3A3220', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
          <div className="stack" style={{ gap: 6 }}>
            <span className="amber small" style={{ fontWeight: 500 }}>
              Waiting for approval on the ops tablet
            </span>
            <span style={{ fontSize: 17, fontWeight: 500 }}>{waiting.summary}</span>
            <span className="muted small">
              Version {waiting.version} · prepared by {waiting.preparedBy} · {when(waiting.preparedAt)}. Nothing changes until it's approved on the ops tablet.
            </span>
          </div>
          <button className="btn small" onClick={() => withdraw(waiting.changeId)}>
            Withdraw
          </button>
        </div>
      ) : (
        <p className="muted">Nothing is waiting for approval.</p>
      )}
      <div className="stack" style={{ gap: 10 }}>
        <span className="card-label">Earlier changes</span>
        <div className="table">
          {earlier.length === 0 && <div className="empty">None yet</div>}
          {earlier.map((c) => (
            <div key={c.changeId} className="tr" style={{ gridTemplateColumns: '150px minmax(0, 1fr) 280px' }}>
              <span className="muted">{when(c.preparedAt)}</span>
              <span className="stack" style={{ gap: 2 }}>
                <span>{c.summary}</span>
                {c.declineNote && <span className="faint small">“{c.declineNote}”</span>}
              </span>
              <span className={tone[c.status]}>
                {word[c.status]}
                {c.decidedBy ? (c.status === 'approved' ? ` on ${c.decidedBy}` : ` by ${c.decidedBy}`) : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
